// 🗑️ Comunicación de Baja (SUNAT - Perú) — Financiero / Configuración
// Anula facturas / notas de crédito electrónicas ya AUTORIZADAS. El usuario selecciona
// los documentos y el motivo; la RPC pe_solicitar_baja encola el lote RA y el worker lo
// envía a SUNAT. Las boletas NO se anulan aquí (van por Resumen Diario). Solo empresas PE.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileX2, Loader2, Ban, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Doc { id: string; serie_comprobante: string; tipo_comprobante: string; razon_social_receptor: string | null; importe_total: number; fecha_emision: string; }
interface Baja { correlativo: number; estado: string; mensaje_sri: string | null; fecha_referencia: string; created_at: string; docs: Array<{ serie: string; numero: number }> | null; }

const ESTADO = {
  pendiente: { icon: Clock, cls: 'text-amber-600', txt: 'Pendiente de envío' },
  enviado: { icon: Clock, cls: 'text-blue-600', txt: 'Enviado; en proceso' },
  aceptado: { icon: CheckCircle2, cls: 'text-green-600', txt: 'Aceptado (anulado)' },
  rechazado: { icon: XCircle, cls: 'text-red-600', txt: 'Rechazado' },
  error: { icon: XCircle, cls: 'text-red-600', txt: 'Error' },
} as const;

const money = (v: number) => new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);

export function ComunicacionBaja() {
  const queryClient = useQueryClient();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [motivo, setMotivo] = useState('');

  const { data: docs = [], isLoading } = useQuery<Doc[]>({
    queryKey: ['bajaDocs'],
    queryFn: async () => (await apiClient.get('/tickets', {
      ...FIN,
      params: {
        estado_emision: 'eq.autorizado', serie_comprobante: 'like.F*',
        select: 'id,serie_comprobante,tipo_comprobante,razon_social_receptor,importe_total,fecha_emision',
        order: 'fecha_emision.desc,serie_comprobante.desc', limit: 200,
      },
    })).data,
  });

  const { data: bajas = [] } = useQuery<Baja[]>({
    queryKey: ['bajaLotes'],
    queryFn: async () => (await apiClient.get('/pe_resumenes', {
      ...FIN,
      params: { tipo: 'eq.RA', select: 'correlativo,estado,mensaje_sri,fecha_referencia,created_at,docs', order: 'created_at.desc', limit: 50 },
    })).data,
    refetchInterval: 15000, // el resultado llega asíncrono desde el worker
  });

  const solicitar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/pe_solicitar_baja', {
      _ticket_ids: [...sel], _motivo: motivo.trim(),
    }, FIN)).data,
    onSuccess: (r: any) => {
      toast.success(r?.message || 'Baja encolada');
      setSel(new Set()); setMotivo('');
      void queryClient.invalidateQueries({ queryKey: ['bajaDocs'] });
      void queryClient.invalidateQueries({ queryKey: ['bajaLotes'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'No se pudo solicitar la baja'),
  });

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (isLoading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <FileX2 className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Comunicación de Baja</h1>
          <p className="text-sm text-muted-foreground">Anula facturas y notas de crédito electrónicas ya autorizadas (SUNAT). Las boletas se anulan por el Resumen Diario.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Documentos autorizados</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay facturas ni notas de crédito autorizadas para anular.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase">
                  <tr><th className="w-10 p-2"></th><th className="p-2 text-left">Documento</th><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Cliente</th><th className="p-2 text-right">Total</th><th className="p-2 text-left">Fecha</th></tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => toggle(d.id)}>
                      <td className="p-2 text-center"><input type="checkbox" className="h-4 w-4 rounded border-input" checked={sel.has(d.id)} onChange={() => toggle(d.id)} onClick={(e) => e.stopPropagation()} /></td>
                      <td className="p-2 font-mono">{d.serie_comprobante}</td>
                      <td className="p-2">{d.tipo_comprobante}</td>
                      <td className="p-2">{d.razon_social_receptor || '—'}</td>
                      <td className="p-2 text-right">{money(d.importe_total)}</td>
                      <td className="p-2">{d.fecha_emision}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium" htmlFor="baja-motivo">Motivo de la baja</label>
            <textarea id="baja-motivo" rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Ej.: Error en el RUC del adquirente" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{sel.size} documento(s) seleccionado(s)</span>
            <Button isLoading={solicitar.isPending} disabled={sel.size === 0 || motivo.trim().length < 3} onClick={() => {
              if (sel.size === 0) { toast.error('Seleccione al menos un documento'); return; }
              if (motivo.trim().length < 3) { toast.error('Indique el motivo de la baja'); return; }
              solicitar.mutate();
            }}>
              {!solicitar.isPending && <Ban className="w-4 h-4" />} Solicitar baja
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Nota: SUNAT exige que todos los documentos de una misma solicitud sean de la misma fecha de emisión.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Bajas solicitadas</CardTitle></CardHeader>
        <CardContent>
          {bajas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aún no se han solicitado bajas.</p>
          ) : (
            <div className="space-y-2">
              {bajas.map((b, i) => {
                const e = ESTADO[b.estado as keyof typeof ESTADO] ?? ESTADO.pendiente;
                const Icon = e.icon;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-md border p-3">
                    <Icon className={`w-5 h-5 shrink-0 ${e.cls}`} />
                    <div className="flex-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">RA #{b.correlativo} · {b.fecha_referencia}</span>
                        <span className={`text-xs ${e.cls}`}>{e.txt}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{(b.docs || []).map((x) => `${x.serie}-${x.numero}`).join(', ') || '—'}</div>
                      {b.mensaje_sri && <div className="text-xs text-muted-foreground mt-1">{b.mensaje_sri}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
